import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  HostListener,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

export interface WsToggleChange {
  checked: boolean;
}

@Component({
  selector: "ws-toggle",
  templateUrl: "./ws-toggle.component.html",
  styleUrls: ["./ws-toggle.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WsToggleComponent),
      multi: true,
    },
  ],
})
export class WsToggleComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() checked: boolean = false;
  @Input() disabled: boolean = false;
  @Input() permission?: string;

  @Output() change: EventEmitter<WsToggleChange> =
    new EventEmitter<WsToggleChange>();

  @ViewChild("slideToggle") slideToggle: MatSlideToggle | undefined;
  @ViewChild("leftContent", { read: ElementRef }) leftContent!: ElementRef;
  @ViewChild("rightContent", { read: ElementRef }) rightContent!: ElementRef;

  // Add these private fields
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor() {}

  ngOnInit(): void {
    // Initialize the checked state based on the input property
    this.checked = this.checked || false;
    this.disabled = this.disabled || false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.checked && !changes.checked.firstChange) {
      this.checked = changes.checked.currentValue;
    }
    if (changes.disabled && !changes.disabled.firstChange) {
      this.disabled = changes.disabled.currentValue;
    }
  }

  clickHandler(event: any): void {
    event.preventDefault();
    event.stopImmediatePropagation(); // Prevent default checkbox behavior
    event.stopPropagation(); // Prevent click event from bubbling up

    if (this.disabled) return;

    const newCheckedState = event.target.checked;

    if (this.checked === newCheckedState) return; // Prevent redundant event emission

    this.checked = newCheckedState;

    // Update Angular form
    this.onChangeFn(this.checked);

    // Emit custom event
    this.change.emit({ checked: this.checked });

    // Mark as touched
    this.onTouchedFn();
  }

  toggleFromLeft(event: any): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (this.checked) return; // Only toggle if unchecked
    this.toggleState(event);
  }

  toggleFromRight(event: any): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!this.checked) return; // Only toggle if checked
    this.toggleState(event);
  }

  private toggleState(event: any): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    this.checked = !this.checked;

    // Notify Angular forms
    this.onChangeFn(this.checked);
    this.onTouchedFn();

    // Emit for external listeners
    this.change.emit({ checked: this.checked });
  }

  // Called by the forms API to write to the view
  writeValue(value: boolean): void {
    this.checked = !!value;
  }

  // Called by the forms API when the value changes
  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }
  // Called by the forms API when the control is touched
  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
